import { z } from "zod";

/**
 * Channels (ARCHITECTURE §6.19): an agent reachable on an outside messaging
 * app. WhatsApp is the only one, linked as a device of a number the user
 * gives the agent. Everyone who writes to that number gets a thread of their
 * own; the agent's user reads every thread, and nobody else reads anyone's.
 */

export const ChannelPlatformSchema = z.enum(["whatsapp"]);
export type ChannelPlatform = z.infer<typeof ChannelPlatformSchema>;

/** A phone number as WhatsApp addresses it: country code and number, digits only, no "+". */
export const PhoneNumberSchema = z.string().regex(/^[1-9]\d{6,14}$/, "a phone number is the country code and number, digits only");
export type PhoneNumber = z.infer<typeof PhoneNumberSchema>;

/** Someone the user knows, by number; the name is the user's, never the one they give themselves. */
export const ChannelContactSchema = z.object({ number: PhoneNumberSchema, name: z.string().trim().min(1).max(80).optional() }).strict();
export type ChannelContact = z.infer<typeof ChannelContactSchema>;

export const CHANNEL_NOTE_MAX_CHARS = 4000;
export const CHANNEL_KNOWN_MAX = 500;
export const CHANNEL_GROUPS_MAX = 100;

/** A WhatsApp group's address: "120363000000000000@g.us", or an older group's "6281…-1600000000@g.us". */
export const GroupAddressSchema = z.string().regex(/^\d{5,25}(-\d{5,15})?@g\.us$/, "a group address is digits ending in @g.us");

/** A group the agent may answer in, under the name it had when the user allowed it. */
export const ChannelGroupSchema = z.object({ address: GroupAddressSchema, name: z.string().trim().min(1).max(120) }).strict();
export type ChannelGroup = z.infer<typeof ChannelGroupSchema>;

/** "09:00": a time of day on the user's clock. */
export const ClockTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "a time of day is HH:MM, 00:00 to 23:59");

/**
 * The hours the agent may offer for meetings, on the user's clock: the days
 * (0 is Sunday) and one span a day. Free times outside them are never shown.
 */
export const MeetingHoursSchema = z
  .object({ days: z.array(z.number().int().min(0).max(6)).max(7), start: ClockTimeSchema, end: ClockTimeSchema })
  .strict()
  .refine((hours) => hours.start < hours.end, { message: "meeting hours end after they start" });
export type MeetingHours = z.infer<typeof MeetingHoursSchema>;

/** Who may hear when the user is free: nobody, the people on their list, or anyone who writes. */
export const FreeTimesAudienceSchema = z.enum(["off", "known", "everyone"]);
export type FreeTimesAudience = z.infer<typeof FreeTimesAudienceSchema>;

/**
 * What the agent may do for people who write, set by its user. A stranger
 * gets a polite reply and a message slip by default, because the user tells
 * people to write; `ignore` keeps them in the list without an answer.
 */
export const ChannelSettingsSchema = z
  .object({
    /** The user's own WhatsApp number: messages from it are the user, in the agent's own chat. */
    ownerNumber: PhoneNumberSchema.optional(),
    /** How people know the user ("Raymond"), so the agent can say whose assistant it is. */
    ownerName: z.string().trim().min(1).max(80).optional(),
    unknown: z.enum(["take_message", "ignore"]),
    known: z.array(ChannelContactSchema).max(CHANNEL_KNOWN_MAX),
    /** Answer in group chats, and there only when mentioned, and only in the groups allowed below. */
    groups: z.boolean(),
    /**
     * The groups the agent answers in. A mention from any other group is kept
     * in its conversation, unanswered, for the user to allow from there: a
     * number added to strangers' groups must not start talking in them.
     */
    allowedGroups: z.array(ChannelGroupSchema).max(CHANNEL_GROUPS_MAX),
    /** What the agent may tell anyone who writes, in the user's words. */
    note: z.string().max(CHANNEL_NOTE_MAX_CHARS),
    /** Send urgent messages on to the user's own number at once. */
    urgentToPhone: z.boolean(),
    /** Who may hear when the user is free, from their calendar: times only, never what the busy times are. */
    freeTimes: FreeTimesAudienceSchema,
    /** People on the list may ask for a meeting: the agent holds the time, and the user books or declines it. */
    meetings: z.boolean(),
    meetingHours: MeetingHoursSchema,
  })
  .strict();
export type ChannelSettings = z.infer<typeof ChannelSettingsSchema>;

export const DEFAULT_MEETING_HOURS: MeetingHours = { days: [1, 2, 3, 4, 5], start: "09:00", end: "17:00" };

export const DEFAULT_CHANNEL_SETTINGS: ChannelSettings = { unknown: "take_message", known: [], groups: false, allowedGroups: [], note: "", urgentToPhone: true, freeTimes: "known", meetings: true, meetingHours: DEFAULT_MEETING_HOURS };

/** PATCH …/channels/:platform/settings: the fields given replace the current ones; `null` clears an optional one. */
export const UpdateChannelSettingsRequestSchema = z
  .object({
    ownerNumber: PhoneNumberSchema.nullable().optional(),
    ownerName: z.string().trim().max(80).nullable().optional(),
    unknown: z.enum(["take_message", "ignore"]).optional(),
    known: z.array(ChannelContactSchema).max(CHANNEL_KNOWN_MAX).optional(),
    groups: z.boolean().optional(),
    allowedGroups: z.array(ChannelGroupSchema).max(CHANNEL_GROUPS_MAX).optional(),
    note: z.string().max(CHANNEL_NOTE_MAX_CHARS).optional(),
    urgentToPhone: z.boolean().optional(),
    freeTimes: FreeTimesAudienceSchema.optional(),
    meetings: z.boolean().optional(),
    meetingHours: MeetingHoursSchema.optional(),
  })
  .strict();
export type UpdateChannelSettingsRequest = z.infer<typeof UpdateChannelSettingsRequestSchema>;

/** Where the link stands. `needs_attention` is a link WhatsApp ended; `problem` says why. */
export const ChannelStateSchema = z.enum(["disconnected", "linking", "connected", "needs_attention"]);
export type ChannelState = z.infer<typeof ChannelStateSchema>;

/**
 * Why a link ended, in the words the row shows. `logged_out` covers the
 * device being removed on the phone and the phone being offline for 14 days,
 * which WhatsApp reports the same way; `blocked` is the number banned.
 */
export const ChannelProblemSchema = z.object({ kind: z.enum(["logged_out", "blocked", "link_expired", "failed"]), detail: z.string().min(1) }).strict();
export type ChannelProblem = z.infer<typeof ChannelProblemSchema>;

/** The code the user's phone links with: a QR on a computer, eight characters to type on a phone. */
export const ChannelLinkSchema = z.discriminatedUnion("method", [
  z.object({ method: z.literal("qr"), qr: z.string().min(1), qrSvg: z.string().min(1) }).strict(),
  z.object({ method: z.literal("code"), phone: PhoneNumberSchema, code: z.string().nullable() }).strict(),
]);
export type ChannelLink = z.infer<typeof ChannelLinkSchema>;

export const ChannelAccountSchema = z.object({ number: z.string().min(1), name: z.string().optional() }).strict();
export type ChannelAccount = z.infer<typeof ChannelAccountSchema>;

/** One channel of one agent, as its row and its pane show it. */
export const ChannelViewSchema = z
  .object({
    platform: ChannelPlatformSchema,
    /** False when this box cannot run the channel at all. */
    available: z.boolean(),
    state: ChannelStateSchema,
    account: ChannelAccountSchema.optional(),
    link: ChannelLinkSchema.optional(),
    problem: ChannelProblemSchema.optional(),
    /** A newly linked number sends slowly until then (epoch ms), so WhatsApp gets used to it. */
    warmingUntil: z.number().int().nonnegative().optional(),
    settings: ChannelSettingsSchema,
  })
  .strict();
export type ChannelView = z.infer<typeof ChannelViewSchema>;

/** GET /agents/:id/channels. */
export const ChannelListResponseSchema = z.object({ channels: z.array(ChannelViewSchema) }).strict();
export type ChannelListResponse = z.infer<typeof ChannelListResponseSchema>;

/** POST …/channels/:platform/link: start linking, by QR or by a code typed on the phone. */
export const StartChannelLinkRequestSchema = z.discriminatedUnion("method", [z.object({ method: z.literal("qr") }).strict(), z.object({ method: z.literal("code"), phone: PhoneNumberSchema }).strict()]);
export type StartChannelLinkRequest = z.infer<typeof StartChannelLinkRequestSchema>;

/** Who a thread is with, which decides what the agent may do in it. */
export const ChannelTierSchema = z.enum(["known", "unknown", "group"]);
export type ChannelTier = z.infer<typeof ChannelTierSchema>;

export const ChannelMessageKindSchema = z.enum(["text", "voice", "image", "file", "other"]);
export type ChannelMessageKind = z.infer<typeof ChannelMessageKindSchema>;

/** One person's (or one group's) conversation with the agent. */
export const ChannelThreadSchema = z
  .object({
    id: z.string().min(1),
    platform: ChannelPlatformSchema,
    /** The user's name for them when they are known, else the one they gave WhatsApp, else their number. */
    name: z.string().min(1),
    /** The number that wrote; absent for a group, and for a sender WhatsApp shows only by a private id. */
    number: PhoneNumberSchema.optional(),
    /** The name they gave themselves on WhatsApp: shown, never trusted. */
    pushName: z.string().optional(),
    tier: ChannelTierSchema,
    /** For a group: whether it is on the user's list, so the agent answers there. */
    allowed: z.boolean().optional(),
    lastText: z.string(),
    lastAt: z.number().int().nonnegative(),
    lastDirection: z.enum(["in", "out"]),
    /** Messages and questions the agent passed to the user that they have not answered yet. */
    waiting: z.number().int().nonnegative(),
    createdAt: z.number().int().nonnegative(),
  })
  .strict();
export type ChannelThread = z.infer<typeof ChannelThreadSchema>;

export const ChannelMessageSchema = z
  .object({
    id: z.string().min(1),
    threadId: z.string().min(1),
    seq: z.number().int().nonnegative(),
    direction: z.enum(["in", "out"]),
    kind: ChannelMessageKindSchema,
    text: z.string(),
    at: z.number().int().nonnegative(),
    /** In a group, who wrote it. */
    sender: z.string().optional(),
    /** Sent because the user asked for it, rather than as the agent's own reply. */
    byOwner: z.boolean().optional(),
    /** Sending failed; the text is what was meant to go. */
    failed: z.boolean().optional(),
  })
  .strict();
export type ChannelMessage = z.infer<typeof ChannelMessageSchema>;

/** GET …/channels/:platform/threads: newest first. */
export const ChannelThreadListResponseSchema = z.object({ threads: z.array(ChannelThreadSchema) }).strict();
export type ChannelThreadListResponse = z.infer<typeof ChannelThreadListResponseSchema>;

/** GET …/channels/:platform/threads/:threadId: the thread and its messages, oldest first. */
export const ChannelThreadResponseSchema = z.object({ thread: ChannelThreadSchema, messages: z.array(ChannelMessageSchema) }).strict();
export type ChannelThreadResponse = z.infer<typeof ChannelThreadResponseSchema>;

/**
 * PATCH …/channels/:platform/threads/:threadId: put a person on the user's
 * list, or take them off, under the user's own name for them; or let the
 * agent answer in a group, or stop.
 */
export const UpdateChannelThreadRequestSchema = z.union([z.object({ known: z.boolean(), name: z.string().trim().min(1).max(80).optional() }).strict(), z.object({ allowed: z.boolean() }).strict()]);
export type UpdateChannelThreadRequest = z.infer<typeof UpdateChannelThreadRequestSchema>;

/**
 * POST /agents/:id/contacts/:entryId: the user answering a question the agent
 * passed on, setting a message slip aside, or booking or declining a meeting
 * the agent is holding, with a word to pass on.
 */
export const AnswerContactRequestSchema = z.union([
  z.object({ answer: z.string().trim().min(1).max(2000) }).strict(),
  z.object({ dismissed: z.literal(true) }).strict(),
  z.object({ meeting: z.enum(["book", "decline"]), message: z.string().trim().min(1).max(2000).optional() }).strict(),
]);
export type AnswerContactRequest = z.infer<typeof AnswerContactRequestSchema>;
